import React, { useEffect } from "react";
import { Box } from "@mui/material";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";

/**
 * Blue radial glow that follows the cursor across the consultation page.
 *
 * Visual reference: Figma node 14715:150297 — "Interactive background glow
 * follow mouse cursor React Framer Motion".
 *
 * Single blue blob with a moderate spring so it smoothly trails the pointer.
 */
const SIZE = 1100;

const BackgroundGlow: React.FC = () => {
  const theme: Theme = useTheme();

  const cursorX = useMotionValue(0);
  const cursorY = useMotionValue(0);

  // One spring config — slow enough to give a "shadow trailing" feel.
  const springConfig = { stiffness: 90, damping: 22, mass: 1 };
  const smoothX = useSpring(cursorX, springConfig);
  const smoothY = useSpring(cursorY, springConfig);

  // Offset by half the size so the blob is centered on the cursor.
  const left = useTransform(smoothX, (x) => x - SIZE / 2);
  const top = useTransform(smoothY, (y) => y - SIZE / 2);

  useEffect(() => {
    // Initialise to viewport center so the glow doesn't jump in from off-screen.
    cursorX.set(window.innerWidth / 2);
    cursorY.set(window.innerHeight / 2);

    const handleMove = (e: PointerEvent) => {
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);
    };
    window.addEventListener("pointermove", handleMove, { passive: true });
    return () => window.removeEventListener("pointermove", handleMove);
  }, [cursorX, cursorY]);

  // Big soft halo following the cursor — subtle.
  const blobBg = `radial-gradient(circle at center, ${theme.colors_ds.PRIMARY_TEXT_MAIN}40 0%, ${theme.colorTokens.P[40]}26 28%, ${theme.colorTokens.P[20]}14 50%, ${theme.colors_ds.NEUTRAL_BG_MAIN}00 70%)`;

  return (
    <Box sx={styles.container}>
      <motion.div
        style={{
          ...styles.blob,
          width: SIZE,
          height: SIZE,
          x: left,
          y: top,
          background: blobBg,
        }}
      />
    </Box>
  );
};

export default BackgroundGlow;

const styles = {
  container: {
    position: "fixed" as const,
    inset: 0,
    overflow: "hidden",
    pointerEvents: "none" as const,
    zIndex: 0,
  },
  blob: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    borderRadius: "50%",
    filter: "blur(120px)",
    willChange: "transform" as const,
  },
};
